/* Mobile product-page "peek" gallery. Independent of Slick entirely — pure scroll-snap
   with an IntersectionObserver just to keep the dots in sync with the visible slide,
   plus a click listener so color swatches on the product page scroll the peek gallery
   to the matching photo. Also implements double-tap-to-zoom on images (in place, no new
   window/modal — CSS transform driven by --zoom-x/--zoom-y/--zoom-scale) and an infinite
   circular loop: a clone of the first slide is appended after the last, and a clone of
   the last slide is prepended before the first, so swiping past either end wraps around;
   once the user's swipe settles on a clone we silently swap the scroll position to the
   matching real slide so it's imperceptible. */
(function () {
  var SEL = {
    root: '[data-pg-peek]',
    track: '[data-pg-peek-track]',
    slide: '[data-pg-peek-slide]',
    dots: '[data-pg-peek-dots]',
    dot: '.pg__mobile-peek__dot',
    img: '.pg__mobile-peek__img'
  };

  var ZOOM_SCALE = 2;
  var TAP_MS = 350;
  var TAP_PX = 30;
  var SCROLL_SETTLE_MS = 120;

  /* 2.10.2026: video slides are marked with a play badge (the dots are left untouched) so the
     shopper can tell the poster frame is a video. The badge stays until the video is
     actually playing, which doubles as the "loading" signal. Styles are injected here
     rather than in pg-slider.css so the whole feature lives in one file. */
  function injectVideoStyles() {
    if (document.getElementById('pg-peek-video-css')) return;
    var css =
      '.pg__mobile-peek__slide--video{position:relative}' +
      '.pg__peek-play{position:absolute;top:50%;left:50%;width:56px;height:56px;margin:-28px 0 0 -28px;' +
        'border-radius:50%;background:rgba(43,40,39,.55);pointer-events:none;z-index:2;transition:opacity .2s}' +
      '.pg__peek-play:after{content:"";position:absolute;top:50%;left:50%;margin:-11px 0 0 -6px;' +
        'border-style:solid;border-width:11px 0 11px 18px;border-color:transparent transparent transparent #fff}' +
      '.pg__mobile-peek__slide--video.is-playing .pg__peek-play{opacity:0}';
    var st = document.createElement('style');
    st.id = 'pg-peek-video-css';
    st.appendChild(document.createTextNode(css));
    document.head.appendChild(st);
  }

  function norm(v) {
    return (v || '').toString().trim().toLowerCase();
  }

  /* Double-tap zoom for a single gallery image. Mirrors Mejuri's pattern: the image is
     transformed in place via translate3d(...) scale(...) driven by CSS custom properties,
     clipped by the slide's own overflow:hidden — never a separate lightbox/window. */
  function initZoom(img) {
    if (!img || img.getAttribute('data-pg-zoom-ready')) return;
    img.setAttribute('data-pg-zoom-ready', '1');

    var lastTapTime = 0;
    var lastTapX = 0;
    var lastTapY = 0;
    var zoomed = false;

    function setVars(scale, x, y) {
      img.style.setProperty('--zoom-scale', scale);
      img.style.setProperty('--zoom-x', x + 'px');
      img.style.setProperty('--zoom-y', y + 'px');
    }

    function resetZoom() {
      if (!zoomed) return;
      zoomed = false;
      setVars(1, 0, 0);
      img.classList.remove('is-zoomed');
    }
    img.pgResetZoom = resetZoom;

    function zoomToPoint(clientX, clientY) {
      var rect = img.getBoundingClientRect();
      var containerW = rect.width;
      var containerH = rect.height;
      if (!containerW || !containerH) return;
      var localX = clientX - rect.left;
      var localY = clientY - rect.top;

      var tx = containerW / 2 - localX * ZOOM_SCALE;
      var ty = containerH / 2 - localY * ZOOM_SCALE;

      var minTx = containerW - containerW * ZOOM_SCALE;
      var minTy = containerH - containerH * ZOOM_SCALE;
      tx = Math.min(0, Math.max(minTx, tx));
      ty = Math.min(0, Math.max(minTy, ty));

      setVars(ZOOM_SCALE, tx, ty);
      img.classList.add('is-zoomed');
      zoomed = true;
    }

    function handleDoubleTap(clientX, clientY) {
      if (zoomed) {
        resetZoom();
      } else {
        zoomToPoint(clientX, clientY);
      }
    }

    img.addEventListener('touchend', function (e) {
      if (!e.changedTouches || !e.changedTouches.length) return;
      var t = e.changedTouches[0];
      var now = Date.now();
      var dx = Math.abs(t.clientX - lastTapX);
      var dy = Math.abs(t.clientY - lastTapY);
      var isDouble = (now - lastTapTime) < TAP_MS && dx < TAP_PX && dy < TAP_PX;
      lastTapTime = isDouble ? 0 : now;
      lastTapX = t.clientX;
      lastTapY = t.clientY;
      if (isDouble) {
        e.preventDefault();
        handleDoubleTap(t.clientX, t.clientY);
      }
    }, { passive: false });

    // Desktop/dev fallback so the behavior is also testable with a mouse.
    img.addEventListener('dblclick', function (e) {
      e.preventDefault();
      handleDoubleTap(e.clientX, e.clientY);
    });
  }

  function initPeek(root) {
    if (!root || root.getAttribute('data-pg-peek-ready')) return;
    var track = root.querySelector(SEL.track);
    var slides = track ? Array.prototype.slice.call(track.querySelectorAll(SEL.slide)) : [];
    root.setAttribute('data-pg-peek-ready', '1');

    // Zoom applies to every slide's image, even when the gallery only has one photo.
    slides.forEach(function (s) {
      var img = s.querySelector(SEL.img);
      if (img) initZoom(img);
    });

    if (!track || slides.length < 2) {
      return;
    }

    var dotsWrap = root.querySelector(SEL.dots);
    var dots = dotsWrap ? Array.prototype.slice.call(dotsWrap.querySelectorAll(SEL.dot)) : [];

    // 2.10.2026: mark video slides with a play badge BEFORE cloning, so the clones
    // carry the badge too. The badge hides once the real video fires "playing".
    var realVideos = [];
    slides.forEach(function (s) {
      var v = s.querySelector('video');
      if (!v) return;
      injectVideoStyles();
      var badge = document.createElement('span');
      badge.className = 'pg__peek-play';
      badge.setAttribute('aria-hidden', 'true');
      s.appendChild(badge);
      v.addEventListener('playing', function () { s.classList.add('is-playing'); });
      v.addEventListener('pause', function () { s.classList.remove('is-playing'); });
      realVideos.push(v);
    });

    // 2.10.2026: start downloading the video on the shopper's FIRST touch/swipe of the
    // gallery, not on page load. Shoppers who never swipe download nothing; those who
    // swipe usually have it buffered by the time they reach the video slide.
    // Only user-generated events count here — forceInitialScroll() below fires a
    // programmatic "scroll" on load, so "scroll" must not trigger the warm-up.
    var warmed = false;
    function warmVideos() {
      if (warmed || !realVideos.length) return;
      warmed = true;
      realVideos.forEach(function (v) {
        if (!v.paused || v.readyState > 1) return;
        v.preload = 'auto';
        try { v.load(); } catch (err) {}
      });
    }
    ['touchstart', 'pointerdown', 'wheel'].forEach(function (evName) {
      track.addEventListener(evName, warmVideos, { passive: true, once: true });
    });

    // --- Infinite loop setup: clone the first slide after the last, and the last slide
    // before the first, so the track always has a slide to swipe to in either direction. ---
    var firstClone = slides[0].cloneNode(true);
    var lastClone = slides[slides.length - 1].cloneNode(true);
    firstClone.setAttribute('data-pg-peek-clone', 'end');
    lastClone.setAttribute('data-pg-peek-clone', 'start');
    firstClone.classList.remove('is-active');
    lastClone.classList.remove('is-active');
    track.insertBefore(lastClone, track.firstChild);
    track.appendChild(firstClone);

    var cloneImgStart = lastClone.querySelector(SEL.img);
    if (cloneImgStart) initZoom(cloneImgStart);
    var cloneImgEnd = firstClone.querySelector(SEL.img);
    if (cloneImgEnd) initZoom(cloneImgEnd);
    // 2.10.2026: cloned <video> elements are NOT load()ed any more. Each load() downloaded
    // the whole video again (~1.5MB per copy). The poster attribute is cloned with the
    // element, so a clone that is briefly visible still shows the first frame.

    var allSlides = [lastClone].concat(slides, [firstClone]);

    function resolveReal(el) {
      var cloneType = el.getAttribute('data-pg-peek-clone');
      if (cloneType === 'start') return slides[slides.length - 1];
      if (cloneType === 'end') return slides[0];
      return el;
    }

    function setActive(slideOrClone) {
      var real = resolveReal(slideOrClone);
      slides.forEach(function (s) {
        s.classList.toggle('is-active', s === real);
        if (s !== real) {
          var img = s.querySelector(SEL.img);
          if (img && img.pgResetZoom) img.pgResetZoom();
        }
      });
      var idx = slides.indexOf(real);
      dots.forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
      // 2.10.2026: the video (preload="none") starts only when its slide is the active one,
      // and pauses when the shopper swipes away — so it downloads only if actually viewed.
      allSlides.forEach(function (s) {
        var v = s.querySelector('video');
        if (!v) return;
        if (s === real) {
          var p = v.play && v.play();
          if (p && p.catch) p.catch(function () {});
        } else if (v.pause) {
          v.pause();
        }
      });
    }

    // Force the natural starting scroll position. In RTL pages, browsers don't agree on
    // what scrollLeft = 0 means for an overflow-x:auto flex container, so without this
    // some browsers land already-scrolled to the last slide — which fully hides the peek
    // sliver even though the CSS widths are correct. scrollIntoView sidesteps the
    // scrollLeft ambiguity entirely (same technique already used for the swatch-click jump).
    function forceInitialScroll() {
      var startSlide = slides[0];
      if (!startSlide) return;
      try {
        startSlide.scrollIntoView({ inline: 'start', block: 'nearest' });
      } catch (err) {
        track.scrollLeft = 0;
      }
      setActive(startSlide);
    }
    if (window.requestAnimationFrame) {
      requestAnimationFrame(function () { requestAnimationFrame(forceInitialScroll); });
    } else {
      setTimeout(forceInitialScroll, 0);
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            setActive(entry.target);
          }
        });
      }, { root: track, threshold: [0.6] });
      allSlides.forEach(function (s) { io.observe(s); });
    }

    // Once the swipe/scroll has fully settled (no scroll events for SCROLL_SETTLE_MS),
    // check whether we landed on a clone. If so, silently reposition the track onto the
    // matching real slide — same visual content, so the swap is invisible — which is what
    // makes the loop feel infinite instead of bouncing off the ends.
    var settleTimer = null;
    track.addEventListener('scroll', function () {
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(function () {
        var trackRect = track.getBoundingClientRect();
        var centerX = trackRect.left + trackRect.width / 2;
        var current = null, bestDist = Infinity;
        allSlides.forEach(function (s) {
          var r = s.getBoundingClientRect();
          var dist = Math.abs((r.left + r.width / 2) - centerX);
          if (dist < bestDist) { bestDist = dist; current = s; }
        });
        if (!current) return;
        var cloneType = current.getAttribute('data-pg-peek-clone');
        if (!cloneType) return;
        var target = cloneType === 'start' ? slides[slides.length - 1] : slides[0];
        try {
          target.scrollIntoView({ inline: 'start', block: 'nearest' });
        } catch (err) {
          track.scrollLeft = target.offsetLeft;
        }
        setActive(target);
      }, SCROLL_SETTLE_MS);
    }, { passive: true });

    // Expose a helper so the swatch-click handler below can jump to a color's slide.
    root.pgPeekGoToColor = function (colorValue) {
      var target = norm(colorValue);
      if (!target) return;
      var match = slides.filter(function (s) {
        return norm(s.getAttribute('data-color-value')) === target;
      })[0];
      if (match) {
        match.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        setActive(match);
      }
    };
  }

  function scan(context) {
    var scope = (context && context.querySelectorAll) ? context : document;
    var roots = scope.querySelectorAll(SEL.root);
    Array.prototype.forEach.call(roots, initPeek);
  }

  // Sync color swatch clicks on the product page to the mobile peek gallery.
  // Mirrors the pattern used for the grid slider in pg-slider.js.
  function bindSwatchSync() {
    document.addEventListener('click', function (e) {
      var swatch = e.target.closest && e.target.closest('.pg__option__value.swatch__item--color');
      if (!swatch) return;

      var peekRoot = document.querySelector(SEL.root);
      if (!peekRoot || typeof peekRoot.pgPeekGoToColor !== 'function') return;

      var colorValue = swatch.getAttribute('data-value') || swatch.getAttribute('data-color-value') || swatch.title || '';
      if (!colorValue) return;

      peekRoot.pgPeekGoToColor(colorValue);
    }, true);
  }

  function boot() {
    scan();
    bindSwatchSync();
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(function () { scan(); });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
